
import React from 'react';
import { X, ChevronLeft, ChevronRight, GalleryVertical } from 'lucide-react';
type GalleryImage = {
  id: number;
  src: string;
  alt: string;
};
type PhotoGalleryProps = {
  images: GalleryImage[];
  title?: string;
  subtitle?: string;
  columns?: 2 | 3 | 4;
  icon?: React.ReactNode;
};
const PhotoGallery = ({
  images,
  title,
  subtitle,
  columns = 3,
  icon
}: PhotoGalleryProps) => {
  const [lightboxOpen, setLightboxOpen] = React.useState(false);
  const [currentImageIndex, setCurrentImageIndex] = React.useState(0);
  const [imgLoadErrors, setImgLoadErrors] = React.useState<Record<number, boolean>>({});

  // Fallback images for error handling
  const fallbackImages = ['/lovable-uploads/4cf849ae-1f20-11f0-a6d9-0242ac110002-19-hero_image.png', '/lovable-uploads/4ccb07aa-1f20-11f0-bef4-0242ac110002-19-hero_image.png', '/lovable-uploads/4d80b050-1f20-11f0-b47d-0242ac110002-19-hero_image.png'];

  // Handle image loading errors
  const handleImageError = (imageId: number, e: React.SyntheticEvent<HTMLImageElement>) => {
    setImgLoadErrors(prev => ({
      ...prev,
      [imageId]: true
    }));
    const fallbackIndex = imageId % fallbackImages.length;
    e.currentTarget.src = fallbackImages[fallbackIndex];
    e.currentTarget.alt = "Cat image (fallback)";
    e.currentTarget.classList.add("fallback-image");
  };

  // Deduplicate images based on src property
  const uniqueImages = React.useMemo(() => {
    const seen = new Set();
    return images.filter(image => {
      const isDuplicate = seen.has(image.src);
      seen.add(image.src);
      return !isDuplicate;
    });
  }, [images]);
  
  const openLightbox = (index: number) => {
    setCurrentImageIndex(index);
    setLightboxOpen(true);
    document.body.style.overflow = 'hidden';
  };
  
  const closeLightbox = () => {
    setLightboxOpen(false);
    document.body.style.overflow = 'auto';
  };
  
  const goToPrevious = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentImageIndex(prev => prev === 0 ? uniqueImages.length - 1 : prev - 1);
  };
  
  const goToNext = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentImageIndex(prev => prev === uniqueImages.length - 1 ? 0 : prev + 1);
  };
  
  const columnsClass = {
    2: 'grid-cols-1 sm:grid-cols-2',
    3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4'
  }[columns];

  // Get the effective source for an image (original or fallback)
  const getEffectiveImageSrc = (image: GalleryImage) => {
    if (imgLoadErrors[image.id]) {
      const fallbackIndex = image.id % fallbackImages.length;
      return fallbackImages[fallbackIndex];
    }
    return image.src;
  };

  // Defensive: If there are no images, show a message
  if (!uniqueImages || uniqueImages.length === 0) {
    return <section className="py-16 bg-[#FAF2E8] relative">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-2xl font-bold mb-4">No images to display.</h2>
          <p className="text-lg text-[#3E2C2F]">Check back soon for more photos from our tours and community!</p>
        </div>
      </section>;
  }
  
  return <section className="py-16 bg-[#FAF2E8] relative">
      <div className="absolute inset-0 paper-texture"></div>
      <div className="container mx-auto px-4 relative z-10">
        {(title || subtitle) && <div className="text-center mb-10">
            {title && <h2 className="text-3xl md:text-4xl font-montserrat font-bold relative pb-4 mb-6 text-[#3E2C2F] text-center">
                {icon && <span className="mr-2">{icon}</span>}
                {title}
              </h2>}
            {subtitle && <p className="text-lg max-w-3xl mx-auto font-montserrat text-[#3E2C2F] px-6">
                {subtitle}
              </p>}
          </div>}
        
        <div className={`grid ${columnsClass} gap-5`}>
          {uniqueImages.map((image, index) => <div key={image.id} className="group relative cursor-pointer overflow-hidden rounded-lg shadow-elegant hover:shadow-premium transition-all duration-500" onClick={() => openLightbox(index)}>
              <div className="aspect-square overflow-hidden border border-[#C58B65]/10 hover:border-[#C58B65]/30 transition-all relative">
                <img 
                  src={getEffectiveImageSrc(image)} 
                  alt={image.alt} 
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  onError={(e) => handleImageError(image.id, e)}
                />
                <div className="absolute inset-0 vignette opacity-40"></div>
              </div>
            </div>)}
        </div>
      </div>
      
      {/* Lightbox */}
      {lightboxOpen && <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4 backdrop-blur-sm" onClick={closeLightbox}>
          <button className="absolute top-4 right-4 text-white hover:text-[#C58B65] z-10 p-2 bg-black/30 rounded-full transition-all duration-300 hover:bg-black/50" onClick={closeLightbox} aria-label="Close lightbox">
            <X size={24} />
          </button>
          
          <button className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:text-[#C58B65] z-10 p-3 bg-black/30 rounded-full transition-all duration-300 hover:bg-black/50" onClick={goToPrevious} aria-label="Previous image">
            <ChevronLeft size={32} />
          </button>
          
          <button className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:text-[#C58B65] z-10 p-3 bg-black/30 rounded-full transition-all duration-300 hover:bg-black/50" onClick={goToNext} aria-label="Next image">
            <ChevronRight size={32} />
          </button>
          
          <div className="max-w-4xl max-h-[90vh] fade-in" onClick={e => e.stopPropagation()}>
            <img src={getEffectiveImageSrc(uniqueImages[currentImageIndex])} alt={uniqueImages[currentImageIndex].alt} className="max-w-full max-h-[80vh] mx-auto object-contain rounded-lg shadow-2xl" onError={e => handleImageError(uniqueImages[currentImageIndex].id, e)} />
            <div className="mt-4 text-center text-white/70 font-montserrat">
              <span>{currentImageIndex + 1} / {uniqueImages.length}</span>
            </div>
          </div>
        </div>}
    </section>;
};
export default PhotoGallery;
